// src/pages/admin/InvoicesList.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminService } from "../../services/admin.service";
import { generateInvoice } from "../../utils/generateInvoice";
import { generateB2BInvoice } from "../../utils/generateB2BInvoice"; // ✅ Imported B2B Utils
import "./InvoiceList.css";

export default function InvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // ✅ New B2B/B2C Filter

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await adminService.getInvoices();
      setInvoices(data);
      setFilteredInvoices(data);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Client-side filtering logic
  useEffect(() => {
    let result = invoices;

    // 1. Search Filter (Added company_name to search)
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.invoice_number?.toLowerCase().includes(lowerQuery) ||
          inv.order_id?.toLowerCase().includes(lowerQuery) ||
          inv.customer_name?.toLowerCase().includes(lowerQuery) ||
          inv.company_name?.toLowerCase().includes(lowerQuery),
      );
    }

    // 2. Status Filter
    if (statusFilter !== "all") {
      result = result.filter((inv) => inv.status === statusFilter);
    }

    // 3. Date Filter
    if (dateFilter !== "all") {
      const now = new Date();
      result = result.filter((inv) => {
        const invDate = new Date(inv.invoice_date || inv.createdAt);
        if (dateFilter === "today") {
          return invDate.toDateString() === now.toDateString();
        }
        if (dateFilter === "month") {
          return (
            invDate.getMonth() === now.getMonth() &&
            invDate.getFullYear() === now.getFullYear()
          );
        }
        return true;
      });
    }

    // 4. Type Filter (B2B vs B2C)
    if (typeFilter !== "all") {
      result = result.filter((inv) => {
        const isB2B = inv.invoice_type === "B2B_MANUAL" || !!inv.customer_gstin;
        return typeFilter === "b2b" ? isB2B : !isB2B;
      });
    }

    setFilteredInvoices(result);
  }, [searchQuery, statusFilter, dateFilter, typeFilter, invoices]);

  // Calculate Metrics for the Dashboard
  const totalRevenue = filteredInvoices.reduce(
    (sum, inv) => sum + Number(inv.grand_total || 0),
    0,
  );
  const totalTax = filteredInvoices.reduce(
    (sum, inv) =>
      sum +
      Number(inv.total_cgst || 0) +
      Number(inv.total_sgst || 0) +
      Number(inv.total_igst || 0),
    0,
  );
  const totalInvoices = filteredInvoices.length;

  // ✅ Smart Download Function
  const downloadPdf = (invoiceRecord) => {
    try {
      const isB2B =
        invoiceRecord.invoice_type === "B2B_MANUAL" ||
        !!invoiceRecord.customer_gstin;
      if (isB2B) {
        generateB2BInvoice(invoiceRecord);
      } else {
        generateInvoice(invoiceRecord);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF.");
    }
  };

  // ✅ Upgraded CSV Export
  const exportToCSV = () => {
    if (filteredInvoices.length === 0) return alert("No data to export");
    const headers = [
      "Invoice No",
      "Date",
      "Type",
      "Company Name",
      "GSTIN",
      "Customer",
      "Order ID",
      "Taxable",
      "IGST",
      "CGST",
      "SGST",
      "Grand Total",
      "Status",
    ];
    const rows = filteredInvoices.map((inv) => [
      inv.invoice_number,
      new Date(inv.invoice_date || inv.createdAt).toLocaleDateString("en-IN"),
      inv.invoice_type === "B2B_MANUAL" || inv.customer_gstin ? "B2B" : "B2C",
      `"${inv.company_name || ""}"`,
      inv.customer_gstin || "",
      `"${inv.customer_name}"`,
      inv.order_id || "Manual Walk-in",
      inv.total_taxable_value || 0,
      inv.total_igst || 0,
      inv.total_cgst || 0,
      inv.total_sgst || 0,
      inv.grand_total || 0,
      inv.status || "GENERATED",
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Invoices_Export_${new Date().toLocaleDateString()}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="invoice-page-wrapper">
      <div className="invoice-header-section">
        <div>
          <h1 className="invoice-page-title">Billing & GST Invoices</h1>
          <p className="invoice-subtitle">
            Manage and track your financial records
          </p>
        </div>
        <div className="invoice-header-actions">
          <button onClick={exportToCSV} className="btn-secondary">
            ⬇ Export CSV
          </button>
          <Link to="/admin/invoices/manual" className="btn-primary">
            + Create New Bill
          </Link>
        </div>
      </div>

      <div className="invoice-metrics-grid">
        <div className="metric-card">
          <div className="metric-title">Total Invoices</div>
          <div className="metric-value">{totalInvoices}</div>
        </div>
        <div className="metric-card">
          <div className="metric-title">Total Collection</div>
          <div className="metric-value text-blue">
            ₹{" "}
            {totalRevenue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-title">GST Collected</div>
          <div className="metric-value text-green">
            ₹ {totalTax.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className="invoice-controls-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search Inv #, Order ID, Customer, Company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          {/* ✅ New B2B / B2C Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="b2b">B2B Only</option>
            <option value="b2c">B2C Only</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="month">This Month</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="GENERATED">Generated</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="REFUNDED">Refunded</option>
          </select>
        </div>
      </div>

      <div className="invoice-table-container">
        <table className="invoice-table">
          <thead>
            <tr>
              <th>Invoice No.</th>
              <th>Date</th>
              <th>Order Ref & Customer</th>
              <th className="text-right">Taxable (₹)</th>
              <th className="text-right">GST (₹)</th>
              <th className="text-right">Grand Total</th>
              <th className="text-center">Status</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center p-8 text-gray-500">
                  Loading records...
                </td>
              </tr>
            ) : filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center p-8 text-gray-500">
                  No invoices match your filters.
                </td>
              </tr>
            ) : (
              filteredInvoices.map((inv) => (
                <tr key={inv.invoice_id || inv.id || inv.invoice_number}>
                  <td className="inv-number">{inv.invoice_number}</td>
                  <td>
                    {new Date(
                      inv.invoice_date || inv.createdAt,
                    ).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>

                  {/* ✅ Smart Customer Column */}
                  <td>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#111827",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {inv.company_name ? inv.company_name : inv.customer_name}
                      {(inv.invoice_type === "B2B_MANUAL" ||
                        inv.customer_gstin) && (
                        <span className="badge-b2b">B2B</span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        marginBottom: "2px",
                      }}
                    >
                      {inv.order_id
                        ? `Order: #${inv.order_id}`
                        : "Manual Walk-in"}
                    </div>
                    {inv.company_name && (
                      <div style={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                        Attn: {inv.customer_name}
                      </div>
                    )}
                  </td>

                  <td className="text-right">
                    {Number(inv.total_taxable_value || 0).toFixed(2)}
                  </td>

                  {/* ✅ Smart Tax Display (Shows IGST if applicable) */}
                  <td className="text-right">
                    {Number(inv.total_igst) > 0 ? (
                      <div className="tax-breakdown">
                        IGST: {Number(inv.total_igst).toFixed(2)}
                      </div>
                    ) : (
                      <>
                        <div className="tax-breakdown">
                          CGST: {Number(inv.total_cgst || 0).toFixed(2)}
                        </div>
                        <div className="tax-breakdown">
                          SGST: {Number(inv.total_sgst || 0).toFixed(2)}
                        </div>
                      </>
                    )}
                  </td>

                  <td className="text-right inv-amount">
                    ₹{" "}
                    {Number(inv.grand_total || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td className="text-center">
                    <span
                      className={`status-badge ${(inv.status || "GENERATED").toLowerCase()}`}
                    >
                      {inv.status || "GENERATED"}
                    </span>
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => downloadPdf(inv)}
                      className="action-btn-download"
                    >
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
